import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";

// The controlled vocabularies of the two power quality studies (EOS-11, Master Prompt A32):
// the power factor correction of one bus (11a) and the harmonic assessment at one point of
// common coupling (11b), copied verbatim from the backend so a value the API can send is
// never rejected here. Sources at commit 5efe9ba, plus CAPACITOR_VOLTAGE_AT_NOMINAL_ONLY
// (IR-005 finding F-1 part 1, 2026-10-16):
// backend/app/domain/electrical/power_quality/results.py (PowerQualityStatus,
// PowerQualityWarningCode and the four code sets, LimitBasis, LimitComparison) and
// .../power_quality/models.py (LoadFiguresSource, ShortCircuitPowerSource,
// DemandCurrentSource). powerQualityContract.test.ts pins each list.
//
// The calculation case and the fault type of the linked fault run are the fault module's
// enums, reused from the fault contract rather than written out a second time.

export const powerQualityStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "INDETERMINATE",
]);

export const powerQualityWarningCodeSchema = z.enum([
  "PF_TARGET_NOT_ABOVE_PRESENT",
  "PF_TARGET_UNITY_OR_LEADING",
  "LOAD_FIGURES_SOURCE_NOT_DECLARED",
  "SHORT_CIRCUIT_POWER_NOT_ESTABLISHED",
  "SHORT_CIRCUIT_SOURCE_NOT_DECLARED",
  "RESONANCE_NEAR_CHARACTERISTIC_ORDER",
  "CAPACITOR_VOLTAGE_ABOVE_RATING",
  "BANK_BELOW_REQUIREMENT",
  "STEP_RESIDUAL_ABOVE_SMALLEST_STEP",
  "LOAD_RUN_NOT_LINKED",
  "CHARACTERISTIC_ORDERS_NOT_DECLARED",
  "STEPS_NOT_DECLARED",
  "REACTOR_RATIO_NOT_DECLARED",
  "CAPACITOR_VOLTAGE_AT_NOMINAL_ONLY",
  "BILLING_CONTEXT_INFORMATION_ONLY",
  "DISPLACEMENT_PF_BASIS",
  "SPECTRUM_SOURCE_NOT_DECLARED",
  "SPECTRUM_ORDER_REPEATED",
  "SPECTRUM_EMPTY",
  "DEMAND_CURRENT_NOT_ESTABLISHED",
  "DEMAND_CURRENT_SOURCE_NOT_DECLARED",
  "HARMONIC_LIMIT_NOT_ESTABLISHED",
  "HARMONIC_LIMIT_DECLARED_NOT_VERIFIED",
  "DISTORTION_ABOVE_LIMIT",
  "IN_PHASE_SUMMATION_ASSUMED",
  "SOURCE_IMPEDANCE_AS_REACTANCE",
]);

export const loadFiguresSourceSchema = z.enum(["LINKED_LOAD_RUN", "TYPED"]);

export const shortCircuitPowerSourceSchema = z.enum(["LINKED_FAULT_RUN", "TYPED"]);

export const demandCurrentSourceSchema = z.enum(["LINKED_LOAD_RUN", "TYPED"]);

export const limitBasisSchema = z.enum(["REGISTER_VERIFIED", "DECLARED", "NOT_ESTABLISHED"]);

export const limitComparisonSchema = z.enum(["WITHIN", "ABOVE", "NOT_EVALUATED"]);

export { faultTypeSchema, shortCircuitCaseSchema };

/** Nothing can be computed: the status is INDETERMINATE (design note section 6). */
export const INDETERMINATE_WARNING_CODES = [
  "PF_TARGET_NOT_ABOVE_PRESENT",
  "SPECTRUM_ORDER_REPEATED",
  "SPECTRUM_EMPTY",
] as const;

/**
 * A source is missing, or a rule the register does not hold yet decides the case (the
 * capacitor voltage above its rating, R1): REVIEW_REQUIRED, never a pass on margin.
 */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "LOAD_FIGURES_SOURCE_NOT_DECLARED",
  "SHORT_CIRCUIT_POWER_NOT_ESTABLISHED",
  "SHORT_CIRCUIT_SOURCE_NOT_DECLARED",
  "CAPACITOR_VOLTAGE_ABOVE_RATING",
  "SPECTRUM_SOURCE_NOT_DECLARED",
  "DEMAND_CURRENT_NOT_ESTABLISHED",
  "DEMAND_CURRENT_SOURCE_NOT_DECLARED",
  "HARMONIC_LIMIT_NOT_ESTABLISHED",
  "HARMONIC_LIMIT_DECLARED_NOT_VERIFIED",
] as const;

/** Computed in full, but the engineer must look: WARNING. */
export const WARNING_CODES = [
  "PF_TARGET_UNITY_OR_LEADING",
  "RESONANCE_NEAR_CHARACTERISTIC_ORDER",
  "BANK_BELOW_REQUIREMENT",
  "STEP_RESIDUAL_ABOVE_SMALLEST_STEP",
  "DISTORTION_ABOVE_LIMIT",
] as const;

/** What the sheet must say; an information code never moves the status. */
export const INFORMATION_WARNING_CODES = [
  "LOAD_RUN_NOT_LINKED",
  "CHARACTERISTIC_ORDERS_NOT_DECLARED",
  "STEPS_NOT_DECLARED",
  "REACTOR_RATIO_NOT_DECLARED",
  "CAPACITOR_VOLTAGE_AT_NOMINAL_ONLY",
  "BILLING_CONTEXT_INFORMATION_ONLY",
  "DISPLACEMENT_PF_BASIS",
  "IN_PHASE_SUMMATION_ASSUMED",
  "SOURCE_IMPEDANCE_AS_REACTANCE",
] as const;

export type PowerQualityStatus = z.infer<typeof powerQualityStatusSchema>;
export type PowerQualityWarningCode = z.infer<typeof powerQualityWarningCodeSchema>;
export type LoadFiguresSource = z.infer<typeof loadFiguresSourceSchema>;
export type ShortCircuitPowerSource = z.infer<typeof shortCircuitPowerSourceSchema>;
export type DemandCurrentSource = z.infer<typeof demandCurrentSourceSchema>;
export type LimitBasis = z.infer<typeof limitBasisSchema>;
export type LimitComparison = z.infer<typeof limitComparisonSchema>;
